import { Image, Page, Text, View } from '@react-pdf/renderer'
import { Badge, Space, Tabs } from 'antd'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { AssetDrawingTabContent } from 'pages/reporting/reports/raut/sections/subSections/assetDrawingComponents/AssetDrawingTabContent'
import { PdfHeatmapWithLegend } from 'pages/reporting/reports/rug/components/PdfHeatmapWithLegend'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement } from 'types'

const DrawingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
`

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 0;

    &::before {
      border-bottom-color: ${({ theme }) =>
        theme.forecast.cardDividerBackground};
    }
  }

  .ant-tabs-tab {
    padding: 0.625rem 1rem;
    margin: 0;
    background: ${({ theme }) => theme.forecast.background2};
    border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
    border-radius: 0.375rem 0.375rem 0 0;
    color: ${({ theme }) => theme.forecast.secondaryText};
    transition: all 0.15s ease-out;

    &:hover {
      color: ${({ theme }) => theme.forecast.defaultText};
      background: ${({ theme }) => theme.forecast.background2_5};
    }

    &.ant-tabs-tab-active {
      background: ${({ theme }) => theme.forecast.cardBackground};
      border-bottom-color: ${({ theme }) => theme.forecast.cardBackground};

      .ant-tabs-tab-btn {
        color: ${({ theme }) => theme.forecast.activeText};
      }
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }
`

const TabContentWrapper = styled.div`
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
`

interface CaptureStatus {
  isCapturing: boolean
  message: string
}

const RenderReact: FC = () => {
  const {
    metadataEntryId,
    inspections,
    componentsByInspection,
    componentModelsByInspection,
    relevantThicknessCDFsByInspection,
    markupLayersByInspection,
    handleAssetDrawingsFinish,
    assetDrawings,
    nominalsByInspection,
    formData,
    componentDefaults,
  } = useRautReportOps()

  const isLocked = useMemo(() => {
    return formData.lockState?.isLocked || false
  }, [formData.lockState])

  const [activeTabKey, setActiveTabKey] = useState<string>(
    inspections[0]?.id || ''
  )

  const selectedComponent = useMemo(
    () => componentsByInspection.get(activeTabKey),
    [componentsByInspection, activeTabKey]
  )
  const selectedComponentModel = useMemo(
    () => componentModelsByInspection.get(activeTabKey),
    [componentModelsByInspection, activeTabKey]
  )
  const relevantThicknessCDF = useMemo(
    () => relevantThicknessCDFsByInspection.get(activeTabKey),
    [relevantThicknessCDFsByInspection, activeTabKey]
  )
  const markupLayers = useMemo(
    () => markupLayersByInspection.get(activeTabKey) || [],
    [markupLayersByInspection, activeTabKey]
  )

  const selectedComponentDefaults = useMemo(
    () =>
      componentDefaults.find(
        (componentDefault) =>
          componentDefault.tags.component === selectedComponent?.id
      ),
    [componentDefaults, selectedComponent]
  )

  const [captureStatusByInspection, setCaptureStatusByInspection] = useState<
    Record<string, CaptureStatus>
  >({})

  const handleCaptureStatusChange = useCallback(
    (status: CaptureStatus) => {
      setCaptureStatusByInspection((prev) => ({
        ...prev,
        [activeTabKey]: status,
      }))
    },
    [activeTabKey]
  )

  const getCaptureProgress = useCallback(
    (inspectionId: string) => {
      const status = captureStatusByInspection[inspectionId]
      const chunkCount = assetDrawings.get(inspectionId)?.chunks.length ?? 0

      return {
        isComplete: chunkCount > 0,
        isInProgress: status?.isCapturing || false,
      }
    },
    [assetDrawings, captureStatusByInspection]
  )

  const tabItems = useMemo(
    () =>
      inspections.map((inspection) => {
        const component = componentsByInspection.get(inspection.id)
        const componentName = component?.data.name || 'Unknown Component'
        const progress = getCaptureProgress(inspection.id)

        return {
          key: inspection.id,
          label: (
            <Space>
              {`${componentName} (${inspection.data.inspection_type})`}
              {progress.isComplete && <Badge status='success' />}
              {progress.isInProgress && <Badge status='processing' />}
            </Space>
          ),
          children: null,
        }
      }),
    [componentsByInspection, getCaptureProgress, inspections]
  )

  const tabContent = useMemo(() => {
    if (!selectedComponent || !selectedComponentModel) {
      return null
    }

    return (
      <AssetDrawingTabContent
        isLocked={isLocked}
        key={activeTabKey}
        inspectionId={activeTabKey}
        component={selectedComponent}
        componentModel={selectedComponentModel}
        thicknessCDF={relevantThicknessCDF}
        markupLayers={markupLayers}
        metadataEntryId={metadataEntryId}
        onDrawingFinish={handleAssetDrawingsFinish}
        onCaptureStatusChange={handleCaptureStatusChange}
        nominal={nominalsByInspection.get(activeTabKey) as GeckoMeasurement}
        componentDefaults={selectedComponentDefaults}
      />
    )
  }, [
    activeTabKey,
    handleAssetDrawingsFinish,
    handleCaptureStatusChange,
    isLocked,
    markupLayers,
    metadataEntryId,
    nominalsByInspection,
    relevantThicknessCDF,
    selectedComponent,
    selectedComponentDefaults,
    selectedComponentModel,
  ])

  return (
    <DrawingContainer>
      <StyledTabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={tabItems}
        size='small'
        type='card'
        tabBarGutter={8}
      />
      <TabContentWrapper>{tabContent}</TabContentWrapper>
    </DrawingContainer>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    assetDrawings: drawingsByInspection,
    componentsByInspection,
  } = useRautReportOps()

  const hasRegisteredAssetDrawing = useRef(false)
  const renderPageTracker = () => (
    <Text
      render={({ pageNumber }) => {
        if (!hasRegisteredAssetDrawing.current) {
          onRender({ id: 'assetDrawing', pageNumber })
          hasRegisteredAssetDrawing.current = true
        }
        return ''
      }}
    />
  )
  const renderFooter = () => (
    <PageFooter
      jobNumber={formData.coverPageData.geckoJobId}
      revisionNumber={formData.revisionMetadata.revisionNumber}
      prelim={
        !(
          formData.signoff?.reviewerSignoff &&
          formData.signoff.supervisorSignoff
        )
      }
    />
  )

  return (
    <>
      {Array.from(drawingsByInspection.entries()).map(
        ([inspectionId, drawings]) => {
          const component = componentsByInspection.get(inspectionId)
          const componentName = component?.data.name || 'Unknown Component'

          return drawings.chunks
            .filter((chunk) => !!chunk.signedUrl)
            .flatMap((chunk) => {
              const titleSuffix =
                drawings.chunks.length > 1 ? ` - View ${chunk.index + 1}` : ''

              return [
                chunk.scanPlanSignedUrl ? (
                  <Page
                    key={`${inspectionId}-${chunk.chunkKey}-scan-plan`}
                    size='A4'
                    orientation='landscape'
                    style={PDFStyles.page}
                    id={
                      !hasRegisteredAssetDrawing.current
                        ? 'assetDrawing'
                        : `assetDrawing-${inspectionId}-${chunk.chunkKey}-scan-plan`
                    }
                    wrap={false}>
                    {renderPageTracker()}
                    <PageHeader title={`Asset Drawings - ${componentName}`} />
                    <View style={PDFStyles.content}>
                      <Text
                        style={{
                          fontSize: 10,
                          fontWeight: 'bold',
                          color: 'black',
                        }}>
                        Asset Drawing - Scan Plan{titleSuffix}
                      </Text>
                      <Text style={PDFStyles.imageDescription}>
                        Asset view without thickness heatmap overlay, showing
                        scan positions for this inspection.
                      </Text>
                      <View style={PDFStyles.imageContainer}>
                        <Image
                          src={chunk.scanPlanSignedUrl}
                          style={PDFStyles.image}
                          cache={false}
                        />
                      </View>
                    </View>
                    {renderFooter()}
                  </Page>
                ) : null,
                <Page
                  key={`${inspectionId}-${chunk.chunkKey}-heatmap`}
                  size='A4'
                  orientation='landscape'
                  style={PDFStyles.page}
                  id={
                    !hasRegisteredAssetDrawing.current
                      ? 'assetDrawing'
                      : `assetDrawing-${inspectionId}-${chunk.chunkKey}-heatmap`
                  }
                  wrap={false}>
                  {renderPageTracker()}
                  <PageHeader title={`Asset Drawings - ${componentName}`} />
                  <View style={PDFStyles.content}>
                    <Text
                      style={{
                        fontSize: 10,
                        fontWeight: 'bold',
                        color: 'black',
                      }}>
                      Asset Drawing - UT Thickness Reading Heat Map{titleSuffix}
                    </Text>
                    <Text style={PDFStyles.imageDescription}>
                      Asset view with thickness heatmap overlay and asset
                      geometry.
                    </Text>
                    <View style={PDFStyles.imageContainer}>
                      <PdfHeatmapWithLegend
                        heatmapSrc={chunk.signedUrl!}
                        legendSrc={chunk.legendSignedUrl}
                      />
                    </View>
                  </View>
                  {renderFooter()}
                </Page>,
              ]
            })
        }
      )}
    </>
  )
}

export const AssetDrawings: ReportSection = {
  id: 'assetDrawing',
  title: 'Asset Drawings',
  description:
    'Generate chunked AssetViz renders for each inspection using the report bundle renderer.',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
